import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ShieldOff, RefreshCw, MonitorX, Clock, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const fecha = (d) => (d ? new Date(d).toLocaleString('es-CL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');

function Tarjeta({ titulo, icono: Icono, color, total, sub, lista, extra, abierta, onToggle, navigate }) {
    const colores = {
        red: 'text-red-600 bg-red-50 dark:bg-red-900/20', amber: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20',
        orange: 'text-orange-600 bg-orange-50 dark:bg-orange-900/20', blue: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20', green: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20',
    };
    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col">
            <button type="button" onClick={onToggle} className="flex items-center gap-3 p-4 text-left" disabled={!lista?.length}>
                <span className={`p-2.5 rounded-lg ${colores[color] || colores.blue}`}><Icono size={20} /></span>
                <span className="flex-1 min-w-0">
                    <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{titulo}</span>
                    <span className="block text-2xl font-bold text-gray-900 dark:text-white">{total}</span>
                    {sub && <span className="block text-xs text-gray-500 dark:text-gray-400">{sub}</span>}
                </span>
                {lista?.length > 0 && (abierta ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />)}
            </button>
            {abierta && lista?.length > 0 && (
                <ul className="border-t border-gray-100 dark:border-gray-700 max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                    {lista.map((e) => (
                        <li key={e.activoId}>
                            <button type="button" onClick={() => navigate(`/activos/${e.codigo}?tab=software`)} className="w-full flex items-center justify-between gap-2 px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700/40">
                                <span className="font-mono text-xs font-semibold text-gray-800 dark:text-gray-100">{e.codigo}</span>
                                <span className="flex-1 truncate text-gray-500 dark:text-gray-400">{e.asignado || 'Sin asignar'}</span>
                                {extra ? <span className="text-xs text-gray-400 whitespace-nowrap">{extra(e)}</span> : e.evidencia ? <span className="text-xs text-gray-400 truncate max-w-[40%]">{e.evidencia}</span> : null}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

/** Cumplimiento de seguridad de los equipos, a partir de la sincronización con Vicarius. */
export default function DashboardSeguridad() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const esAdmin = user?.role?.toLowerCase() === 'admin';
    const [datos, setDatos] = useState(null);
    const [sync, setSync] = useState(null);
    const [error, setError] = useState('');
    const [abierta, setAbierta] = useState(null);
    const timer = useRef(null);

    const cargar = useCallback(async () => {
        try {
            const r = await api.get('/vicarius/cumplimiento');
            setDatos(r.data);
            setError('');
        } catch (e) {
            setError(e.response?.status === 404 ? '' : 'No se pudo cargar el cumplimiento de seguridad.');
        }
    }, []);

    const consultarSync = useCallback(async () => {
        try {
            const r = await api.get('/vicarius/sincronizacion');
            setSync(r.data);
            if (r.data.enCurso) timer.current = setTimeout(consultarSync, 5000);
            else if (timer.current) { timer.current = null; cargar(); }
        } catch { /* sin acceso */ }
    }, [cargar]);

    useEffect(() => { cargar(); consultarSync(); return () => clearTimeout(timer.current); }, [cargar, consultarSync]);

    const sincronizarTodo = async () => {
        try {
            const r = await api.post('/vicarius/sincronizar');
            setSync(r.data);
            consultarSync();
        } catch (e) {
            setError(e.response?.data?.message || 'No se pudo iniciar la sincronización.');
        }
    };

    if (error && !datos) return <p className="text-sm text-red-500">{error}</p>;
    if (!datos) return null;

    const cob = datos.totalEquipos ? Math.round((datos.conVicarius / datos.totalEquipos) * 100) : 0;
    const iconos = { 'Vicarius vRx': ShieldCheck };

    return (
        <section className="space-y-3">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Cumplimiento de seguridad</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        {datos.conVicarius} de {datos.totalEquipos} equipos con Vicarius ({cob}%) · datos al {fecha(datos.ultimaSincronizacion)}
                        {sync?.enCurso ? ` · sincronizando ${sync.procesados}/${sync.total}…` : ''}
                    </p>
                </div>
                {esAdmin && (
                    <button type="button" onClick={sincronizarTodo} disabled={sync?.enCurso}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-60">
                        <RefreshCw size={14} className={sync?.enCurso ? 'animate-spin' : ''} /> Sincronizar ahora
                    </button>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
                <Tarjeta titulo="Sin Vicarius" icono={ShieldOff} color="red" total={datos.sinVicarius.total} sub="No aparecen en Vicarius"
                    lista={datos.sinVicarius.lista} abierta={abierta === 'sin'} onToggle={() => setAbierta(abierta === 'sin' ? null : 'sin')} navigate={navigate} />
                {datos.faltantes.filter((f) => f.programa !== 'Vicarius vRx').map((f) => (
                    <Tarjeta key={f.programa} titulo={`Sin ${f.programa}`} icono={iconos[f.programa] || ShieldOff} color="orange" total={f.total} sub="Programa requerido no detectado"
                        lista={f.lista} abierta={abierta === f.programa} onToggle={() => setAbierta(abierta === f.programa ? null : f.programa)} navigate={navigate} />
                ))}
                <Tarjeta titulo="Sistema desactualizado" icono={MonitorX} color="amber" total={datos.soDesactualizado.total} sub="Windows 10 o anterior"
                    lista={datos.soDesactualizado.lista} extra={(e) => e.sistema} abierta={abierta === 'so'} onToggle={() => setAbierta(abierta === 'so' ? null : 'so')} navigate={navigate} />
                <Tarjeta titulo="Sin reportar" icono={Clock} color="amber" total={datos.sinReportar.total} sub={`Más de ${datos.sinReportar.dias} días sin conectarse`}
                    lista={datos.sinReportar.lista} extra={(e) => fecha(e.ultimoReporte)} abierta={abierta === 'rep'} onToggle={() => setAbierta(abierta === 'rep' ? null : 'rep')} navigate={navigate} />
                <Tarjeta titulo="Riesgo alto" icono={AlertTriangle} color="red" total={datos.riesgoAlto.total} sub="Nivel de riesgo High o Critical en Vicarius"
                    lista={datos.riesgoAlto.lista} extra={(e) => (e.vulnerabilidades != null ? `${e.vulnerabilidades} vuln.` : '')} abierta={abierta === 'rie'} onToggle={() => setAbierta(abierta === 'rie' ? null : 'rie')} navigate={navigate} />
            </div>

            {datos.programas?.length > 0 && (
                <>
                    <h3 className="pt-2 text-sm font-semibold text-gray-700 dark:text-gray-200">Equipos que sí lo tienen instalado</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 items-start">
                        {datos.programas.map((p) => (
                            <Tarjeta key={p.programa} titulo={`Con ${p.programa}`} icono={ShieldCheck} color="green" total={p.instalados.total}
                                sub={`de ${datos.totalEquipos} equipos`} lista={p.instalados.lista}
                                abierta={abierta === `ok-${p.programa}`} onToggle={() => setAbierta(abierta === `ok-${p.programa}` ? null : `ok-${p.programa}`)} navigate={navigate} />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}
